import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Sparkles, RefreshCw, Info, PlayCircle, TrendingUp,
  Save, Plus, Lock,
} from "lucide-react";

/*
 * v85.42 — Halaman Estimasi Durasi
 *   • TOP: Tabel "Durasi Tetap" (editable, admin-controlled) — PRIORITAS 0
 *     di backend. Cell diklik → input menit → klik "Simpan Semua" utk persist.
 *   • BOTTOM: Tabel v2 "Histori Billing" (read-only reference, tidak lagi
 *     memengaruhi scheduler). Tombol "Jalankan Analisa" tetap ada utk refresh.
 *
 * Label alias (display-only): kunci backend tetap canonical (CLEANING / AC_INSTALL /
 *   AC_REPAIR) agar scheduler, pricing, warranty, dsb tetap jalan. Yang berubah hanya
 *   label yang terlihat admin di UI tabel.
 */
const SERVICE_LABEL_MAP = {
  CLEANING: "SERVICE",
  AC_INSTALL: "PASANG",
  AC_REPAIR: "PERBAIKAN",
};
const displayServiceType = (st) => SERVICE_LABEL_MAP[st] || st;

export default function Estimasi() {
  // ─── Fixed Duration table state ───────────────────────────────
  const [fx, setFx] = useState(null);         // {service_types, max_qty, matrix}
  const [fxLoading, setFxLoading] = useState(false);
  const [fxSaving, setFxSaving] = useState(false);
  const [fxDirty, setFxDirty] = useState({}); // "{st}__{qty}" → "120" (string, raw input)
  const [newServiceOpen, setNewServiceOpen] = useState(false);
  const [newServiceName, setNewServiceName] = useState("");

  // ─── v2 analytics reference ──────────────────────────────────
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [days, setDays] = useState(15);
  const [lastAnalysis, setLastAnalysis] = useState(null);

  async function loadFx() {
    setFxLoading(true);
    try {
      const r = await api.durationFixedGet();
      setFx(r);
      setFxDirty({});
    } catch (e) {
      toast.error("Gagal load durasi tetap: " + (e?.message || "error"));
    } finally {
      setFxLoading(false);
    }
  }

  async function saveFx() {
    if (!fx) return;
    const cells = [];
    for (const key of Object.keys(fxDirty)) {
      const [st, qs] = key.split("__");
      const q = parseInt(qs, 10);
      const raw = fxDirty[key];
      const minutes = raw === "" ? null : parseInt(raw, 10);
      if (raw !== "" && (!Number.isFinite(minutes) || minutes <= 0)) {
        toast.error(`Nilai menit tidak valid di ${displayServiceType(st)} / ${q} unit`);
        return;
      }
      cells.push({ service_type: st, qty: q, minutes });
    }
    if (cells.length === 0) {
      toast.info("Belum ada perubahan untuk disimpan");
      return;
    }
    setFxSaving(true);
    try {
      const r = await api.durationFixedSave({ cells });
      toast.success(`✓ Tersimpan: ${r.saved} cell, dihapus: ${r.deleted}`);
      await loadFx();
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || e.message));
    } finally {
      setFxSaving(false);
    }
  }

  async function addServiceType() {
    const name = (newServiceName || "").trim().toUpperCase().replace(/\s+/g, "_");
    if (!name) { toast.error("Nama layanan wajib"); return; }
    // Cegah duplikat canonical + alias (mis. user ketik "SERVICE" padahal
    // "CLEANING" sudah ada → keduanya tampil sama)
    const existingAliasForNew = Object.entries(SERVICE_LABEL_MAP)
      .find(([, alias]) => alias === name)?.[0];
    const dupCanonical = fx?.service_types?.includes(name);
    const dupAlias = existingAliasForNew && fx?.service_types?.includes(existingAliasForNew);
    if (dupCanonical || dupAlias) {
      toast.error("Layanan sudah ada");
      return;
    }
    try {
      await api.durationFixedSave({ cells: [], add_service_types: [name] });
      toast.success(`✓ Layanan "${name}" ditambahkan`);
      setNewServiceName("");
      setNewServiceOpen(false);
      await loadFx();
    } catch (e) {
      toast.error("Gagal: " + (e?.response?.data?.detail || e.message));
    }
  }

  function cellValue(st, q) {
    const key = `${st}__${q}`;
    if (key in fxDirty) return fxDirty[key];
    return (fx?.matrix?.[st]?.[q] ?? "").toString();
  }

  function onCellChange(st, q, val) {
    const v = (val || "").replace(/[^\d]/g, "");
    setFxDirty(prev => ({ ...prev, [`${st}__${q}`]: v }));
  }

  async function loadV2() {
    setLoading(true);
    try { setData(await api.estimateTableV2()); }
    catch (e) { toast.error("Gagal load v2: " + (e?.message || "error")); }
    finally { setLoading(false); }
  }

  async function runAnalysis() {
    if (analyzing) return;
    setAnalyzing(true);
    try {
      const r = await api.estimatorAnalyzeV2(days);
      setLastAnalysis(r);
      toast.success(`Analisa v2 selesai: ${r.samples_collected} sample dari ${r.wo_scanned} WO`,
                     { duration: 4000 });
      await loadV2();
    } catch (e) {
      toast.error("Analisa gagal: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setAnalyzing(false);
    }
  }

  useEffect(() => { loadFx(); loadV2(); }, []);

  const qtyRange = useMemo(() => {
    const max = fx?.max_qty || 15;
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [fx?.max_qty]);

  const dirtyCount = Object.keys(fxDirty).length;

  return (
    <div className="space-y-6" data-testid="estimasi-page">
      {/* ── TOP: Fixed Duration Table (admin-controlled) ── */}
      <div className="border border-emerald-300 bg-emerald-50/40 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-['Manrope'] font-bold text-[14px] mb-1 text-emerald-900">
              Durasi Tetap — Prioritas Tertinggi (Admin-Controlled)
            </div>
            <div className="text-[12px] text-zinc-700 leading-relaxed">
              Klik tiap sel untuk mengisi menit secara langsung. Nilai di sini akan menjadi
              <b> sumber pertama</b> saat sistem meng-estimasi durasi WO; <b>bypass</b> semua
              histori otomatis. Kosongkan sel kalau mau pakai fallback histori/seed.
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={saveFx} disabled={fxSaving || dirtyCount === 0}
            className="rounded-sm gap-1.5 bg-emerald-600 hover:bg-emerald-700"
            data-testid="btn-save-fixed">
            <Save className={`w-3.5 h-3.5 ${fxSaving?'animate-pulse':''}`} />
            Simpan Semua {dirtyCount > 0 && `(${dirtyCount})`}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setNewServiceOpen(v => !v)}
            className="rounded-sm gap-1.5" data-testid="btn-add-service">
            <Plus className="w-3.5 h-3.5" />Tambah Layanan
          </Button>
          <Button size="sm" variant="outline" onClick={loadFx}
            className="rounded-sm gap-1.5" data-testid="btn-refresh-fixed">
            <RefreshCw className={`w-3.5 h-3.5 ${fxLoading?'animate-spin':''}`} />Refresh
          </Button>
          {newServiceOpen && (
            <div className="flex items-center gap-1.5 ml-2">
              <Input value={newServiceName} onChange={e => setNewServiceName(e.target.value)}
                placeholder="Nama baru (contoh: PASANG_BARU)" className="h-8 w-56 text-[12px] rounded-sm"
                data-testid="new-service-input"
                onKeyDown={e => e.key === "Enter" && addServiceType()}
              />
              <Button size="sm" onClick={addServiceType} className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-add-service">
                Tambah
              </Button>
            </div>
          )}
          <div className="ml-auto text-[11px] text-zinc-500 font-mono">
            {fx?.service_types?.length || 0} layanan × {fx?.max_qty || 0} qty
          </div>
        </div>
      </div>

      {/* Fixed table */}
      <div className="border border-border bg-white rounded-sm overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="fixed-duration-table">
          <thead className="bg-emerald-50 border-b border-emerald-200 sticky top-0">
            <tr className="text-left text-[10px] uppercase tracking-widest text-emerald-800">
              <th className="px-3 py-2.5 sticky left-0 bg-emerald-50 z-10 min-w-[160px]">Jenis Layanan</th>
              {qtyRange.map(q => (
                <th key={q} className="px-2 py-2.5 text-center min-w-[80px]">{q} Unit</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(fx?.service_types || []).map(st => (
              <tr key={st} className="border-b border-border last:border-0 hover:bg-emerald-50/30">
                <td className="px-3 py-2 font-mono text-[11px] font-semibold sticky left-0 bg-white z-10">
                  {displayServiceType(st)}
                </td>
                {qtyRange.map(q => {
                  const val = cellValue(st, q);
                  const isDirty = `${st}__${q}` in fxDirty;
                  const hasValue = val !== "" && val !== undefined && val !== null;
                  return (
                    <td key={q} className="px-1 py-1 text-center"
                      data-testid={`fx-cell-${st}-${q}`}>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={val}
                        onChange={e => onCellChange(st, q, e.target.value)}
                        placeholder="—"
                        className={`w-full h-9 px-1 text-center font-mono text-[12px] rounded-sm outline-none border transition-colors
                          ${isDirty
                            ? "bg-amber-100 border-amber-400 text-amber-900 font-bold"
                            : hasValue
                              ? "bg-emerald-100 border-emerald-300 text-emerald-900 font-bold"
                              : "bg-zinc-50 border-zinc-200 text-zinc-400 hover:border-emerald-300"}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            {(fx?.service_types || []).length === 0 && (
              <tr>
                <td colSpan={qtyRange.length + 1} className="px-4 py-8 text-center text-zinc-500 text-[12px]">
                  Belum ada layanan. Klik <b>"Tambah Layanan"</b> di atas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-zinc-500 flex items-start gap-2 bg-zinc-50 p-3 rounded-sm">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <div>
          <b>Prioritas estimasi durasi di backend:</b>
          <ol className="mt-1 space-y-0.5 list-decimal list-inside">
            <li><b>Durasi Tetap</b> (manual admin) ← tabel di atas, sumber utama</li>
            <li>Histori ICS Google Calendar (≥3 sampel) — fallback 1</li>
            <li>Actual duration dari WO in-app (≥3 sampel) — fallback 2</li>
            <li>Seed table hardcode, scheduled proxy, default 60 mnt/unit</li>
          </ol>
          <div className="mt-1 text-amber-700">
            Catatan: tabel "Histori Billing v2" di bawah tetap tersedia sebagai referensi analisa,
            tapi <b>sudah tidak memengaruhi scheduler</b>. Admin bebas pakai hasilnya sebagai acuan utk isi tabel atas.
          </div>
        </div>
      </div>

      {/* ── BOTTOM: v2 Analytics (reference-only) ── */}
      <div className="border border-[#2962FF]/20 bg-[#2962FF]/5 rounded-sm p-4 mt-6">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#2962FF] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-['Manrope'] font-bold text-[14px] mb-1">
              Estimasi Durasi v2 — Berbasis Histori Billing (REFERENSI SAJA)
            </div>
            <div className="text-[12px] text-zinc-700 leading-relaxed">
              Analisa otomatis dari billing items yang teknisi input, utk jadi referensi
              angka saat admin mengisi "Durasi Tetap" di atas. Sudah tidak dipakai scheduler
              secara langsung.
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <label className="text-[11px] font-semibold text-zinc-600">Sample window:</label>
          <Input type="number" value={days}
            onChange={e => setDays(Math.max(3, Math.min(60, parseInt(e.target.value || "15"))))}
            className="h-8 w-16 text-[12px] rounded-sm" min={3} max={60}
            data-testid="days-input" />
          <span className="text-[11px] text-zinc-500">hari</span>
          <Button size="sm" onClick={runAnalysis} disabled={analyzing}
            className="rounded-sm gap-1.5 bg-[#2962FF] hover:bg-[#1E4FCC]"
            data-testid="btn-analyze">
            <PlayCircle className={`w-3.5 h-3.5 ${analyzing?'animate-pulse':''}`} />
            {analyzing ? "Menganalisa…" : "Jalankan Analisa"}
          </Button>
          <Button variant="outline" size="sm" onClick={loadV2} className="rounded-sm gap-1.5"
            data-testid="est-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading?'animate-spin':''}`} />Refresh Tabel
          </Button>
          <div className="ml-auto flex gap-1.5">
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 rounded-sm text-[10px]">
              KONVERGEN
            </Badge>
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 rounded-sm text-[10px]">
              BELUM STABIL
            </Badge>
          </div>
        </div>
        {lastAnalysis && (
          <div className="mt-3 text-[11px] text-zinc-600 flex items-center gap-3 flex-wrap font-mono">
            <span>WO scanned: <b>{lastAnalysis.wo_scanned}</b></span>
            <span>Sample: <b>{lastAnalysis.samples_collected}</b></span>
            <span className="text-emerald-600">Buckets: <b>{lastAnalysis.buckets_saved}</b></span>
          </div>
        )}
      </div>

      <div className="border border-border bg-white rounded-sm overflow-hidden">
        <table className="w-full text-[13px]" data-testid="estimasi-v2-table">
          <thead className="bg-zinc-50 border-b border-border">
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
              <th className="px-4 py-2.5">Jenis Layanan</th>
              {[1,2,3,4,5].map(q => (
                <th key={q} className="px-4 py-2.5 text-center">{q} Unit</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.rows?.map(row => (
              <tr key={row.job_category} className="border-b border-border last:border-0 hover:bg-zinc-50/60">
                <td className="px-4 py-3">
                  <div className="font-semibold text-[13px]">{row.label}</div>
                  <div className="text-[10px] font-mono text-zinc-400">{row.job_category}</div>
                </td>
                {["1","2","3","4","5"].map(q => (
                  <td key={q} className="px-4 py-2 text-center" data-testid={`cell-${row.job_category}-${q}`}>
                    <QtyCell data={row.per_qty[q]} />
                  </td>
                ))}
              </tr>
            ))}
            {(!data?.rows || data.rows.length === 0) && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-zinc-500 text-[12px]">
                  Belum ada data v2. Klik "Jalankan Analisa" untuk refresh.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function QtyCell({ data }) {
  if (!data) {
    return <span className="text-zinc-400 font-mono">—</span>;
  }
  const conv = data.converged;
  const color = conv ? "text-emerald-700 border-emerald-300 bg-emerald-50"
                     : "text-amber-700 border-amber-300 bg-amber-50";
  return (
    <div className={`inline-flex flex-col items-center px-2 py-1 rounded border ${color}`}>
      <div className="font-mono font-bold text-[13px] leading-tight">
        {formatMinutes(data.minutes)}
      </div>
      <div className="text-[9px] font-mono opacity-70 leading-tight flex items-center gap-1">
        <TrendingUp className="w-2.5 h-2.5" />
        {data.sample_count}× · {Math.round(data.convergence_pct)}%
      </div>
    </div>
  );
}

function formatMinutes(m) {
  if (!m) return "-";
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min}m`;
  if (min === 0) return `${h}j`;
  return `${h}j${min}m`;
}
